import { useEffect } from "react";

const SITE_ORIGIN = "https://www.atmasammanschoolofai.com";
const DEFAULT_OG_IMAGE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_asai-core/artifacts/91eihqrm_ASAI%20LOGO.png";
const DEFAULT_TITLE = "ASAI – Atmasamman School of AI | Learn Today. Lead Tomorrow.";
const DEFAULT_DESCRIPTION =
  "ASAI – Atmasamman School of AI. Industry-focused programs and internships in Artificial Intelligence, Generative AI, Machine Learning, Cloud, DevOps, UPI and Digital Payments. Based in Bulandshahr, Uttar Pradesh, India.";

// --- helpers ---
const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, obj) => {
  document.querySelectorAll(`script[data-jsonld="${id}"]`).forEach((n) => n.remove());
  if (!obj) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-jsonld", id);
  s.textContent = JSON.stringify(obj);
  document.head.appendChild(s);
};

/**
 * Full-featured SEO hook.
 *
 * Signature (backward compatible):
 *   useSeo("About")
 *   useSeo("About", "Meta description")
 *   useSeo({ title, description, path, ogImage, breadcrumbs, jsonLd })
 *
 * breadcrumbs = [{ name, path }] — path relative to origin.
 * jsonLd      = additional structured data object to append (e.g. Course schema).
 */
export const useSeo = (arg1, arg2) => {
  const opts = typeof arg1 === "object" && arg1 !== null
    ? arg1
    : { title: arg1, description: arg2 };
  const {
    title, description, path,
    ogImage = DEFAULT_OG_IMAGE,
    breadcrumbs, jsonLd, noindex = false,
  } = opts;

  useEffect(() => {
    const fullTitle = title ? `${title} | ASAI – Atmasamman School of AI` : DEFAULT_TITLE;
    document.title = fullTitle;

    const desc = description || DEFAULT_DESCRIPTION;
    const canonicalPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;

    setMeta("name", "description", desc);
    setMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    // Open Graph
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "ASAI – Atmasamman School of AI");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:locale", "en_IN");

    // Twitter card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", ogImage);

    // Canonical
    setLink("canonical", canonicalUrl);

    // Breadcrumbs JSON-LD
    if (breadcrumbs && breadcrumbs.length > 0) {
      setJsonLd("breadcrumbs", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${SITE_ORIGIN}${b.path}`,
        })),
      });
    } else {
      setJsonLd("breadcrumbs", null);
    }

    // Page-specific JSON-LD
    setJsonLd("page", jsonLd || null);
  }, [title, description, path, ogImage, JSON.stringify(breadcrumbs), JSON.stringify(jsonLd), noindex]);
};

export const SEO_DEFAULTS = { SITE_ORIGIN, DEFAULT_OG_IMAGE };
